import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, TrendingUp, ArrowUpRight, ArrowDownRight,
  RefreshCw, Loader, CheckCircle, AlertTriangle, Target, X,
  Zap, PieChart, BarChart3, Shield, ChevronDown, ChevronUp,
  Gauge, SlidersHorizontal
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';
import BudgetPacing from './BudgetPacing';

export default function BudgetAllocator({ selectedAccount }) {
  const { theme } = useTheme();
  const { showToast } = useToast();

  const [campaigns, setCampaigns] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState({});
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('allocator');
  const [demandCampaigns, setDemandCampaigns] = useState([]);
  const [demandSummary, setDemandSummary] = useState(null);
  const [demandLoading, setDemandLoading] = useState(false);
  const [demandSortBy, setDemandSortBy] = useState('priority');
  const [demandSortDir, setDemandSortDir] = useState('asc');
  const [sortBy, setSortBy] = useState('tier');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const response = await apiCall(`/api/budget-allocator/analyze?days=${days}`, { timeout: 50000 });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
        setRecommendations(data.recommendations || []);
        setSummary(data.summary || null);
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      showToast(`Failed to analyze budgets: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, days, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchDemandData = useCallback(async () => {
    if (!selectedAccount) return;
    setDemandLoading(true);
    try {
      const response = await apiCall(`/api/budget-allocator/demand-pacing?days=${days}`, { timeout: 65000 });
      const data = await response.json();
      if (data.success) {
        setDemandCampaigns(data.campaigns || []);
        setDemandSummary(data.summary || null);
      } else {
        throw new Error(data.error || 'Demand pacing failed');
      }
    } catch (err) {
      showToast(`Failed to analyze demand pacing: ${err.message}`, 'error');
    } finally {
      setDemandLoading(false);
    }
  }, [selectedAccount, days, showToast]);

  useEffect(() => {
    if (activeTab === 'demand' && demandCampaigns.length === 0) {
      fetchDemandData();
    }
  }, [activeTab, demandCampaigns.length, fetchDemandData]);

  const handleApply = async (rec) => {
    setApplying(prev => ({ ...prev, [rec.id]: true }));
    try {
      const response = await apiCall('/api/budget-allocator/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: rec.campaignId, newBudget: rec.newBudget }),
        timeout: 35000
      });
      const data = await response.json();
      if (data.success) {
        showToast(`Budget updated to €${rec.newBudget}/day for "${rec.campaignName}"`, 'success');
        setRecommendations(prev => prev.filter(r => r.id !== rec.id));
        setCampaigns(prev => prev.map(c =>
          c.id === rec.campaignId ? { ...c, dailyBudget: rec.newBudget } : c
        ));
      } else {
        throw new Error(data.error || 'Failed to apply');
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, 'error');
    } finally {
      setApplying(prev => ({ ...prev, [rec.id]: false }));
    }
  };

  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'tier') return (a.tier - b.tier) * dir;
    if (sortBy === 'budget') return (a.dailyBudget - b.dailyBudget) * dir;
    if (sortBy === 'spend') return (a.dailySpend - b.dailySpend) * dir;
    if (sortBy === 'pacing') return (a.pacing - b.pacing) * dir;
    if (sortBy === 'conversions') return (a.conversions - b.conversions) * dir;
    if (sortBy === 'budgetLostIS') return (a.budgetLostIS - b.budgetLostIS) * dir;
    return 0;
  });

  const priorityRank = { high: 0, medium: 1, low: 2 };
  const actionLabels = {
    scale_up: 'Scale up',
    reduce: 'Reduce',
    hold: 'Hold',
    test_only: 'Test only',
    fix_efficiency_first: 'Fix first'
  };
  const actionColors = {
    scale_up: '#10b981',
    reduce: '#ef4444',
    hold: '#6b7280',
    test_only: '#f59e0b',
    fix_efficiency_first: '#f59e0b'
  };

  const sortedDemandCampaigns = [...demandCampaigns].sort((a, b) => {
    const dir = demandSortDir === 'asc' ? 1 : -1;
    if (demandSortBy === 'priority') {
      return ((priorityRank[a.recommendation?.priority] ?? 2) - (priorityRank[b.recommendation?.priority] ?? 2)) * dir;
    }
    if (demandSortBy === 'cost') return ((a.cost || 0) - (b.cost || 0)) * dir;
    if (demandSortBy === 'demand') return ((a.scores?.demand || 0) - (b.scores?.demand || 0)) * dir;
    if (demandSortBy === 'efficiency') return ((a.scores?.efficiency || 0) - (b.scores?.efficiency || 0)) * dir;
    if (demandSortBy === 'risk') return ((a.scores?.risk || 0) - (b.scores?.risk || 0)) * dir;
    if (demandSortBy === 'headroom') return ((a.scores?.headroom || 0) - (b.scores?.headroom || 0)) * dir;
    return 0;
  });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'tier' ? 'asc' : 'desc'); }
  };

  const handleDemandSort = (col) => {
    if (demandSortBy === col) setDemandSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else {
      setDemandSortBy(col);
      setDemandSortDir(col === 'priority' ? 'asc' : 'desc');
    }
  };

  const tierColors = { 1: '#10b981', 2: '#f59e0b', 3: '#6b7280' };
  const tierLabels = { 1: 'Proven', 2: 'Scaling', 3: 'Testing' };
  const prioColors = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };

  const styles = {
    container: { padding: 24, maxWidth: 1400, margin: '0 auto' },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: theme.colors.text.primary, margin: 0 },
    subtitle: { fontSize: 14, color: theme.colors.text.secondary, marginTop: 4 },
    controls: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 },
    card: { background: theme.colors.bg.secondary, borderRadius: 12, border: `1px solid ${theme.colors.border.primary}`, padding: 20, marginBottom: 16 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
    stat: { background: theme.colors.bg.secondary, borderRadius: 12, border: `1px solid ${theme.colors.border.primary}`, padding: 16, textAlign: 'center' },
    statValue: { fontSize: 24, fontWeight: 700, color: theme.colors.text.primary },
    statLabel: { fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, borderBottom: `1px solid ${theme.colors.border.primary}`, cursor: 'pointer', userSelect: 'none' },
    td: { padding: '10px 12px', fontSize: 13, color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` },
    badge: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${color}15`, color }),
    button: (variant = 'default') => ({
      padding: '8px 16px', borderRadius: 8, border: variant === 'default' ? `1px solid ${theme.colors.border.primary}` : 'none',
      background: variant === 'primary' ? '#6366f1' : variant === 'success' ? '#10b981' : variant === 'danger' ? '#ef4444' : theme.colors.bg.secondary,
      color: variant === 'default' ? theme.colors.text.primary : '#fff',
      fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6
    }),
    recCard: { background: theme.colors.bg.secondary, borderRadius: 12, border: `1px solid ${theme.colors.border.primary}`, padding: 16, marginBottom: 12, transition: 'all 0.2s' },
    healthBar: (score) => ({
      height: 8, borderRadius: 4, background: theme.colors.border.primary, position: 'relative', overflow: 'hidden',
    }),
    healthFill: (score) => ({
      height: '100%', borderRadius: 4, width: `${score}%`,
      background: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'
    }),
    select: { padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.primary, color: theme.colors.text.primary, fontSize: 13 },
    tabs: { display: 'inline-flex', gap: 4, padding: 4, borderRadius: 10, border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.secondary, marginTop: 16 },
    tab: (active) => ({
      padding: '8px 14px',
      border: 'none',
      borderRadius: 8,
      background: active ? '#6366f1' : 'transparent',
      color: active ? '#fff' : theme.colors.text.secondary,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    }),
    scorePill: (score) => ({
      display: 'inline-flex',
      minWidth: 34,
      justifyContent: 'center',
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: score >= 70 ? 'rgba(16, 185, 129, 0.12)' : score >= 45 ? 'rgba(245, 158, 11, 0.12)' : 'rgba(239, 68, 68, 0.12)',
      color: score >= 70 ? '#10b981' : score >= 45 ? '#f59e0b' : '#ef4444'
    })
  };

  const renderDemandPacing = () => (
    <>
      {demandLoading && demandCampaigns.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: 48 }}>
          <Loader size={30} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: theme.colors.text.secondary, marginTop: 12 }}>Analyzing market demand and budget pacing...</p>
        </div>
      ) : (
        <>
          {demandSummary && (
            <div style={styles.statsGrid}>
              <div style={styles.stat}>
                <div style={styles.statValue}>{demandSummary.campaignCount}</div>
                <div style={styles.statLabel}>Campaigns analyzed</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#10b981' }}>{demandSummary.scaleUpCount || 0}</div>
                <div style={styles.statLabel}>Scale opportunities</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#ef4444' }}>{demandSummary.reduceCount || 0}</div>
                <div style={styles.statLabel}>Reduce recommendations</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#f59e0b' }}>{demandSummary.fixEfficiencyCount || 0}</div>
                <div style={styles.statLabel}>Fix before scaling</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statValue}>€{demandSummary.totalCost?.toFixed(0) || 0}</div>
                <div style={styles.statLabel}>Spend ({days}d)</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statValue}>{Math.round(demandSummary.totalConversions || 0)}</div>
                <div style={styles.statLabel}>Conversions ({days}d)</div>
              </div>
            </div>
          )}

          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TrendingUp size={18} color="#6366f1" /> Demand Pacing Advisor
                </h2>
                <p style={{ fontSize: 13, color: theme.colors.text.secondary, margin: '6px 0 0' }}>
                  Advice-only budget guidance based on recent demand, efficiency, impression-share headroom and risk signals.
                </p>
              </div>
              <button style={styles.button()} onClick={fetchDemandData} disabled={demandLoading}>
                {demandLoading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
                Refresh
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, minWidth: 220 }}>Campaign</th>
                    <th style={styles.th}>Advice</th>
                    <th style={styles.th} onClick={() => handleDemandSort('cost')}>Spend</th>
                    <th style={styles.th}>Budget/day</th>
                    <th style={styles.th} onClick={() => handleDemandSort('demand')}>Demand</th>
                    <th style={styles.th} onClick={() => handleDemandSort('efficiency')}>Efficiency</th>
                    <th style={styles.th} onClick={() => handleDemandSort('headroom')}>Headroom</th>
                    <th style={styles.th} onClick={() => handleDemandSort('risk')}>Risk</th>
                    <th style={styles.th}>Trends</th>
                    <th style={{ ...styles.th, minWidth: 280 }}>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDemandCampaigns.map(c => {
                    const rec = c.recommendation || {};
                    const color = actionColors[rec.action] || '#6b7280';
                    return (
                      <tr key={c.id}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: theme.colors.text.secondary }}>
                            {c.contextTags?.length ? c.contextTags.join(' · ') : 'generic campaign'} · {c.conversions} conv · {c.conversionRate}% CVR
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(color)}>
                            {rec.action === 'scale_up' ? <ArrowUpRight size={10} /> : rec.action === 'reduce' || rec.action === 'test_only' ? <ArrowDownRight size={10} /> : <Target size={10} />}
                            {actionLabels[rec.action] || rec.action}
                          </span>
                          {rec.suggestedChangePct !== 0 && (
                            <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 4 }}>
                              {rec.suggestedChangePct > 0 ? '+' : ''}{rec.suggestedChangePct}% → €{rec.suggestedBudget}/day
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>€{c.cost.toFixed(0)}</td>
                        <td style={styles.td}>€{c.dailyBudget.toFixed(2)}</td>
                        <td style={styles.td}><span style={styles.scorePill(c.scores?.demand || 0)}>{c.scores?.demand || 0}</span></td>
                        <td style={styles.td}><span style={styles.scorePill(c.scores?.efficiency || 0)}>{c.scores?.efficiency || 0}</span></td>
                        <td style={styles.td}><span style={styles.scorePill(c.scores?.headroom || 0)}>{c.scores?.headroom || 0}</span></td>
                        <td style={styles.td}><span style={styles.scorePill(100 - (c.scores?.risk || 0))}>{c.scores?.risk || 0}</span></td>
                        <td style={styles.td}>
                          <div style={{ fontSize: 11, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
                            Impr. {c.trends?.impressions > 0 ? '+' : ''}{c.trends?.impressions}%<br />
                            Clicks {c.trends?.clicks > 0 ? '+' : ''}{c.trends?.clicks}%<br />
                            Conv. {c.trends?.conversions > 0 ? '+' : ''}{c.trends?.conversions}%
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.5 }}>
                            {rec.reasoning}
                          </div>
                          <div style={{ fontSize: 11, color: theme.colors.text.tertiary, marginTop: 6 }}>
                            {rec.targetBasis} · IS lost budget {c.budgetLostIS}%
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {demandCampaigns.length === 0 && !demandLoading && (
              <div style={{ textAlign: 'center', padding: 40, color: theme.colors.text.secondary }}>
                <PieChart size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                <p>No demand pacing data found for enabled campaigns.</p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  if (activeTab === 'allocator' && loading && campaigns.length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={32} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: theme.colors.text.secondary, marginTop: 12 }}>Analyzing campaign budgets...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Budget Allocator</h1>
        <p style={styles.subtitle}>Optimize budget distribution across campaigns based on performance tiers</p>
        {(activeTab === 'allocator' || activeTab === 'demand') && (
          <div style={styles.controls}>
            <select style={styles.select} value={days} onChange={e => setDays(parseInt(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              style={styles.button()}
              onClick={activeTab === 'demand' ? fetchDemandData : fetchData}
              disabled={activeTab === 'demand' ? demandLoading : loading}
            >
              {(activeTab === 'demand' ? demandLoading : loading)
                ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>
        )}
        <div style={styles.tabs}>
          <button style={styles.tab(activeTab === 'allocator')} onClick={() => setActiveTab('allocator')}>
            Budget Allocation
          </button>
          <button style={styles.tab(activeTab === 'demand')} onClick={() => setActiveTab('demand')}>
            Demand Pacing
          </button>
          <button style={styles.tab(activeTab === 'pacing')} onClick={() => setActiveTab('pacing')}>
            <Gauge size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            Pacing
          </button>
          <button style={styles.tab(activeTab === 'planner')} onClick={() => setActiveTab('planner')}>
            <SlidersHorizontal size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            Scenario Planner
          </button>
        </div>
      </div>

      {activeTab === 'demand' ? renderDemandPacing() : activeTab === 'pacing' ? (
        <BudgetPacing key="pacing" selectedAccount={selectedAccount} embedded initialTab="pacing" />
      ) : activeTab === 'planner' ? (
        <BudgetPacing key="planner" selectedAccount={selectedAccount} embedded initialTab="planner" />
      ) : (
        <>
          {/* Summary Stats */}
          {summary && (
            <div style={styles.statsGrid}>
              <div style={styles.stat}>
                <div style={styles.statValue}>€{summary.totalDailyBudget.toFixed(0)}</div>
                <div style={styles.statLabel}>Daily Budget</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statValue}>€{summary.totalDailySpend.toFixed(0)}</div>
                <div style={styles.statLabel}>Avg. Daily Spend</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: summary.overallPacing >= 80 ? '#10b981' : summary.overallPacing >= 50 ? '#f59e0b' : '#ef4444' }}>
                  {summary.overallPacing}%
                </div>
                <div style={styles.statLabel}>Budget Pacing</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statValue}>{Math.round(summary.totalConversions)}</div>
                <div style={styles.statLabel}>Conversions ({days}d)</div>
              </div>
              <div style={styles.stat}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={styles.badge(tierColors[1])}>{summary.tiers.tier1}</span>
                  <span style={styles.badge(tierColors[2])}>{summary.tiers.tier2}</span>
                  <span style={styles.badge(tierColors[3])}>{summary.tiers.tier3}</span>
                </div>
                <div style={styles.statLabel}>Tier 1 / 2 / 3</div>
              </div>
              <div style={styles.stat}>
                <div style={{ marginBottom: 4 }}>
                  <div style={styles.healthBar(summary.healthScore)}>
                    <div style={styles.healthFill(summary.healthScore)} />
                  </div>
                </div>
                <div style={{ ...styles.statValue, fontSize: 18 }}>{summary.healthScore}/100</div>
                <div style={styles.statLabel}>Budget Health</div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={18} color="#6366f1" /> Recommendations ({recommendations.length})
          </h2>
          {recommendations.map(rec => (
            <div key={rec.id} style={styles.recCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={styles.badge(prioColors[rec.priority])}>
                      {rec.priority === 'high' ? <AlertTriangle size={10} /> : <Target size={10} />}
                      {rec.priority}
                    </span>
                    {rec.type === 'increase_budget' ? (
                      <span style={styles.badge('#10b981')}><ArrowUpRight size={10} /> Increase</span>
                    ) : (
                      <span style={styles.badge('#ef4444')}><ArrowDownRight size={10} /> Decrease</span>
                    )}
                  </div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary, margin: '4px 0' }}>{rec.title}</h4>
                  <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '4px 0' }}>{rec.description}</p>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: theme.colors.text.secondary }}>
                    <span>€{rec.currentBudget}/day → <strong style={{ color: theme.colors.text.primary }}>€{rec.newBudget}/day</strong></span>
                    <span style={{ color: '#6366f1' }}>{rec.estimatedImpact}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={styles.button()}
                    onClick={() => setRecommendations(prev => prev.filter(r => r.id !== rec.id))}
                  >
                    <X size={14} /> Dismiss
                  </button>
                  <button
                    style={styles.button('primary')}
                    onClick={() => handleApply(rec)}
                    disabled={applying[rec.id]}
                  >
                    {applying[rec.id] ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                    Apply
                  </button>
                </div>
              </div>
            </div>
          ))}
            </div>
          )}

          {/* Campaign Table */}
          <div style={styles.card}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart3 size={18} color="#6366f1" /> Campaign Overview
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th} onClick={() => handleSort('tier')}>
                  Tier {sortBy === 'tier' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={{ ...styles.th, minWidth: 180 }}>Campaign</th>
                <th style={styles.th} onClick={() => handleSort('budget')}>
                  Budget/day {sortBy === 'budget' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={styles.th} onClick={() => handleSort('spend')}>
                  Avg Spend/day {sortBy === 'spend' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={styles.th} onClick={() => handleSort('pacing')}>
                  Pacing {sortBy === 'pacing' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={styles.th} onClick={() => handleSort('conversions')}>
                  Conv. {sortBy === 'conversions' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={styles.th} onClick={() => handleSort('budgetLostIS')}>
                  IS Lost (Budget) {sortBy === 'budgetLostIS' && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </th>
                <th style={styles.th}>CPA / ROAS</th>
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.map(c => (
                <tr key={c.id} style={{ opacity: c.tier === 3 ? 0.7 : 1 }}>
                  <td style={styles.td}>
                    <span style={styles.badge(tierColors[c.tier])}>
                      {c.tier === 1 ? <Shield size={10} /> : c.tier === 2 ? <TrendingUp size={10} /> : <Target size={10} />}
                      {tierLabels[c.tier]}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: theme.colors.text.secondary }}>{c.tierReason}</div>
                  </td>
                  <td style={styles.td}>€{c.dailyBudget.toFixed(2)}</td>
                  <td style={styles.td}>€{c.dailySpend.toFixed(2)}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 40, height: 6, borderRadius: 3, background: theme.colors.border.primary, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, c.pacing)}%`, height: '100%', borderRadius: 3, background: c.pacing >= 80 ? '#10b981' : c.pacing >= 50 ? '#f59e0b' : '#ef4444' }} />
                      </div>
                      <span style={{ fontSize: 12 }}>{c.pacing}%</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: c.conversions > 0 ? 600 : 400, color: c.conversions > 0 ? '#10b981' : theme.colors.text.secondary }}>
                      {Math.round(c.conversions * 10) / 10}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: c.budgetLostIS > 10 ? '#ef4444' : c.budgetLostIS > 5 ? '#f59e0b' : '#10b981' }}>
                      {c.budgetLostIS}%
                    </span>
                  </td>
                  <td style={styles.td}>
                    {c.cpa && <span>€{c.cpa.toFixed(0)} CPA</span>}
                    {c.roas && <span style={{ marginLeft: c.cpa ? 8 : 0 }}>{c.roas.toFixed(1)}x ROAS</span>}
                    {!c.cpa && !c.roas && <span style={{ color: theme.colors.text.secondary }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {campaigns.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: 40, color: theme.colors.text.secondary }}>
            <DollarSign size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
            <p>No active campaigns found. Make sure campaigns are enabled.</p>
          </div>
        )}
          </div>
        </>
      )}
    </div>
  );
}
